import React, {useState, useCallback, useEffect, useMemo} from 'react';
import { useLocation, useNavigate, useOutletContext } from 'react-router-dom';

import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import List from '@mui/material/List';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import Link from '@mui/material/Link';
import { Menu, MenuItem, IconButton } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Tooltip } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';


import AddSamplesFileListItem from './AddSamplesFileListItem';
import SamplesList from './SamplesList';
import ExportIdentifiedSpeciesMenuItem from '../IdentifiedSpecies/ExportIdentifiedSpeciesMenuItem';
import ExportKgASTPredictionsMenuItem from '../IdentifiedSpecies/ExportKgASTPredictionsMenuItem';
import ExportResistanceMarkersMenuItem from '../IdentifiedSpecies/ExportResistanceMarkersMenuItem';
import FileDropZone from './AddSamplesDropZone';
import UploadIcon from '@mui/icons-material/Upload';

import ontLogo from '../ont_logo.svg';
import illuminaLogo from '../illumina_logo.svg';
import { useActiveAnalysis, useSamples } from '../services/api/KeynomeAPIHooks';
import ArchiveDatasetDialog from './ArchiveSampleDialog';
import { useCreateSample, useCreateConcatenatedFastqgz } from '../services/api/KeynomeAPIMutations';

function isIlluminaDataset(activeRun) {
    if (!activeRun) {
        return false;
    }

    return activeRun.attributes.sequencing_platform === 'illumina'
        || activeRun.attributes.platform === 'illumina';
}

function extractFileBasename(filename) {
    return filename
    .replace(/_001\.fastq\.gz$/, '.fastq.gz')
    .replace('.fastq.gz', '')
    .replace('./', '');
}

function basenameIsR1File(basename) {  
    return basename.match(/_(R?1)(_001)?$/) !== null;
}

function extractSampleNameFromFilename(filename, isIllumina) {
    const basename = extractFileBasename(filename)
    if (isIllumina) {
        return basename.replace(/_(R?[12])(_001)?$/, '');
    }

    return basename
}

function shouldHideSample(sample, analysis, isIllumina) {
    const sampleName = sample?.attributes?.sample_name || '';
    const isUnclassified = sampleName.toLowerCase().includes('unclassified');

    if (isUnclassified) {
        return true;
    }

    if (!analysis || analysis?.attributes?.status !== 'succeeded') {
        return false;
    }

    const r1Megabases = Number(analysis?.attributes?.raw_total_megabases ?? 0);
    const r2Megabases = isIllumina ? Number(analysis?.attributes?.raw_total_megabases_r2 ?? 0) : 0;

    return (r1Megabases + r2Megabases) < 1.0;
}

function AddSamplesDialog(props) {
    const { 
        open, 
        setOpen, 
        activeRun, 
        uploadFile, 
        backgroundUploadingFilesCt, 
        activeProject, 
        setReloadRuns,
        setVisibleRuns,
    } = props;
    const [ archiveDialogOpen, setArchiveDialogOpen ] = useState(false);
    const [ stagedFilesByDataset, setStagedFilesbyDataset] = useState({});
    const [ confirmClose, setConfirmClose ] = useState(false);
    const [ uploadingAll, setUploadingAll ] = useState(false);
    const [ allowLowQuality, setAllowLowQuality ] = useState(false);
    const [ showLowQualityDialog, setShowLowQualityDialog ] = useState(false);
    const [ showHiddenSamples, setShowHiddenSamples ] = useState(false);

    const { setUserErrorMessage } = useOutletContext();
    const { data: samples, refetch: refetchSamples } = useSamples(activeRun?.id);
    const { data: allAnalyses, isLoading: analysesLoading } = useActiveAnalysis(
        samples?.map(sample => sample.id)
    );
    const { mutateAsync: createSample } = useCreateSample();
    const { mutateAsync: createConcatenatedFastqgz } = useCreateConcatenatedFastqgz();

    const isIllumina = isIlluminaDataset(activeRun);

    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        if (activeRun) {
            setStagedFilesbyDataset(prev => {
                const newStagedFiles = {...prev};
                if (!newStagedFiles[activeRun.id]) newStagedFiles[activeRun.id] = {};
                return newStagedFiles;
            })
        }
    }, [activeRun, setStagedFilesbyDataset])

    useEffect(() => {
        if (open) {
            setShowHiddenSamples(false);
        }
    }, [open]);

    // Adds staged file object to stagedFilesByDataset state object by datasetId and sampleName
    // If sampleName does not exist, create a new object with the file
    // If sampleName exists, update the file object with the new file
    // To be used on file drop event
    const addStagedFile = useCallback(file => {
        setStagedFilesbyDataset(prev => {
            const newStagedFiles = {...prev};
            const sampleName = extractSampleNameFromFilename(file.name, isIllumina)

            if (!newStagedFiles[activeRun.id][sampleName]) {
                newStagedFiles[activeRun.id][sampleName] = {
                    datasetId: activeRun.id,
                    sampleName: sampleName,
                    R1: null,
                    R2: null,
                    sampleType: 'isolate',
                    status: null,
                    canUpload: null,
                }
            }
            const fileKey = isIllumina && !basenameIsR1File(extractFileBasename(file.name)) ? 'R2' : 'R1';
            newStagedFiles[activeRun.id][sampleName][fileKey] = file;
            return newStagedFiles;
        })
    }, [setStagedFilesbyDataset, activeRun, isIllumina])

    // Deletes staged file object from stagedFilesByDataset state object by datasetId and sampleName
    // To be used when upload cancelled
    const removeStagedFile = useCallback((datasetId, sampleName) => {
        setStagedFilesbyDataset(prev => {
            const newStagedFiles = {...prev};
            delete newStagedFiles[datasetId][sampleName];
            return newStagedFiles;
        })
    }, [setStagedFilesbyDataset])

    // Generic method to set an attribute on a staged file object by datasetId and sampleName
    // To be used to set the sampleType, status, and canUpload attributes
    const setStagedFileAttribute = useCallback((datasetId, sampleName, attribute, value) => {
        setStagedFilesbyDataset(prev => {
            const newStagedFiles = {...prev};
            if (newStagedFiles[datasetId][sampleName]) newStagedFiles[datasetId][sampleName][attribute] = value;
            return newStagedFiles;
        })
    }, [setStagedFilesbyDataset])

    const stagedFilesForActiveRun = useMemo(
        () => stagedFilesByDataset[activeRun?.id] || {},
        [stagedFilesByDataset, activeRun?.id]
    );

    const runUploadSample = useCallback(async (stagedFile) => {
        const { sampleName, sampleType, R1: file, R2: r2File } = stagedFile;

        try {
            setStagedFileAttribute(activeRun.id, sampleName, 'status', 'creating_lib_seq');
            let newSample;
            try {
                newSample = await createSample(
                    { runId: activeRun.id, sampleType, sampleName, file }
                );
            } catch (error) {
                if (error.message?.includes('422')) {
                    setUserErrorMessage('Failed to create sample record in Pathosight! Please confirm that the sample name is less than 100 characters and try again. Contact pathosight-support@cepheid.com if you require assistance.');
                } else {
                    setUserErrorMessage('Failed to create sample record in Pathosight! Please wait a few minutes and try again. Contact pathosight-support@cepheid.com if you require assistance.');
                }
                throw error;
            }

            setStagedFileAttribute(activeRun.id, sampleName, 'status', 'creating_concatenated_fastqgz');
            const extraFields = ['location_signed_upload_url', ...(isIllumina ? ['location_r2_signed_upload_url'] : [])];

            let newConcatenatedFastqgz;
            try {
                newConcatenatedFastqgz = await createConcatenatedFastqgz(
                    { libSeqId: newSample.id, queryParams: { extraFields } }
                );
            } catch (error) {
                setUserErrorMessage('Failed to create FASTQ record in Pathosight! Please wait a few minutes and try again. Contact pathosight-support@cepheid.com if you require assistance.');
                throw error;
            }

            setStagedFileAttribute(activeRun.id, sampleName, 'status', 'uploading_file');
            await uploadFile(newConcatenatedFastqgz, file, r2File, allowLowQuality);
            setStagedFileAttribute(activeRun.id, sampleName, 'status', 'success');
            await refetchSamples();
        } catch (error) {
            setStagedFileAttribute(activeRun.id, sampleName, 'status', 'failed');
            console.error(error);
        }
    }, [activeRun, createSample, createConcatenatedFastqgz, uploadFile, setStagedFileAttribute, isIllumina, setUserErrorMessage, refetchSamples, allowLowQuality]);

    const getUploadableStagedFiles = useCallback(() => {
        if (!activeRun) return [];
        return Object.values(stagedFilesForActiveRun).filter((stagedFile) => {
            if (stagedFile.status !== null) return false;
            if (stagedFile.canUpload === false) return false;
            if (!stagedFile.R1) return false;
            if (isIllumina && !stagedFile.R2) return false;
            return true;
        });
    }, [activeRun, stagedFilesForActiveRun, isIllumina]);

    const handleUploadAll = useCallback(async () => {
        const uploadable = getUploadableStagedFiles();
        if (!uploadable.length) return;
        setUploadingAll(true);
        await Promise.allSettled(uploadable.map((stagedFile) => runUploadSample(stagedFile)));
        setUploadingAll(false);
    }, [getUploadableStagedFiles, runUploadSample]);
    const handleLowQualityToggle = (event) => {
        const checked = event.target.checked;
        if (checked) {
            setShowLowQualityDialog(true);
        } else {
            setAllowLowQuality(false);
        }
    };

    const visibleSamples = (samples || []).filter(sample => 
        !stagedFilesByDataset[activeRun?.id] ||
        !stagedFilesByDataset[activeRun.id][sample.attributes.sample_name]
        || stagedFilesByDataset[activeRun.id][sample.attributes.sample_name].status === 'success' // show samples were staged and upload completed
        || stagedFilesByDataset[activeRun.id][sample.attributes.sample_name].canUpload === false // show samples that were analyzed successfully and incorrectly re-staged
    );

    const analysisBySampleId = new Map(
        (allAnalyses || [])
            .filter(analysis => analysis?.attributes?.sample_id)
            .map(analysis => [analysis.attributes.sample_id, analysis])
    );

    const hiddenSamples = visibleSamples.filter(sample => (
        shouldHideSample(sample, analysisBySampleId.get(sample.id), isIllumina)
    ));

    const displayedSamples = showHiddenSamples
        ? visibleSamples
        : visibleSamples.filter(sample => (
            !shouldHideSample(sample, analysisBySampleId.get(sample.id), isIllumina)
        ));

    const getStagedFilesNotUploadedCt = () => {
        return Object.keys(stagedFilesByDataset[activeRun?.id] || []).filter(sampleName => {
            const stagedFile = stagedFilesByDataset[activeRun.id][sampleName];
            return stagedFile && stagedFile.status === null && stagedFile.canUpload !== false;
        }).length;
    }
    const uploadableCount = getUploadableStagedFiles().length;
    const uploadAllDisabled = uploadingAll || uploadableCount === 0;
    const handleClose = () => {
        if (getStagedFilesNotUploadedCt() > 0) {
            setConfirmClose(true);
        } else {
            handleConfirmClose();
        }
    }
    const handleConfirmClose = () => {
        setConfirmClose(false);
        setOpen(false);
        // remove all staged files that have not been uploaded or that have been succeeded/failed
        let newStagedFiles = Object.values(stagedFilesByDataset[activeRun.id]).reduce((acc, file) => {
            if (file.status === null || ['failed', 'success'].includes(file.status)) {
                return acc;
            }
            acc[file.sampleName] = file;
            return acc;
        }, {});
        setStagedFilesbyDataset(prev => {
            const newStagedFilesByDataset = {...prev};
            newStagedFilesByDataset[activeRun.id] = newStagedFiles;
            return newStagedFilesByDataset;
        });
        // Update the URL to go back to just the project level 
        const url = new URL(activeProject.attributes.keynome_web_url);
        navigate(url.pathname, { state: location.state }); 
    }

    return (
        <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="xl"
        data-cy="add-samples-dialog"
        >
            <DialogTitle>
                <Grid container>
                    <Grid item xs={8}>
                        {activeRun?.attributes.name}
                         <AddSamplesDialogMenu activeRun={activeRun} isIllumina={isIllumina} setArchiveDialogOpen={setArchiveDialogOpen}/>
                    </Grid>
                    <ArchiveDatasetDialog
                        activeProject={activeProject}
                        activeRun={activeRun}
                        setVisibleRuns={setVisibleRuns}
                        archiveDialogOpen={archiveDialogOpen}
                        setArchiveDialogOpen={setArchiveDialogOpen}
                        setReloadRuns={setReloadRuns}
                        handleClose={handleClose}
                    />
                    <Grid item xs={4} sx={{textAlign: "right"}}>
        { isIllumina ? (
                        <>
                        <img src={illuminaLogo} alt="platform-logo" height="30px" style={{marginBottom: '-8px', marginLeft: '4px', marginRight: '10px'}} />
                        Illumina Paired End
            </>

        ) : (
            <>
                        <img src={ontLogo} alt="platform-logo" height="30px" style={{marginBottom: '-8px', marginLeft: '4px', marginRight: '10px'}} />
                        ONT Single End
            </>
        )}
                    </Grid>
                </Grid>
      <Grid item xs={12}>
        <Box>
            <Grid container>
              <Grid item xs={6}>
                    <div style={{color: '#797979', marginBottom: '20px', marginTop: '20px', fontSize: 15}}>
                        <b>UPLOAD SEQUENCING FILES</b>
                    </div>
              </Grid>
              <Grid item xs={6} sx={{textAlign: "right"}}>
            <div style={{color: '#797979', marginBottom: '20px', marginTop: '20px', fontSize: 15}}>
                Pathosight CLI Upload ID: <pre style={{display: "inline", backgroundColor: "#eee", padding: "5px 10px", borderRadius: "5px"}} >{activeRun?.attributes.uuid}</pre>
            </div>
            </Grid>
            </Grid>
            </Box>
            </Grid>
                <FileDropZone isIllumina={isIllumina} addStagedFile={addStagedFile} />
    {Object.keys(stagedFilesByDataset[activeRun?.id] || []).length ? (
    <>
      <Box sx={{ backgroundColor: '#eee', padding: '1px 10px', color: 'black', marginTop: '20px', fontSize: 15 }}>
        <Grid container sx={{ pl: 1, pr: 1, textAlign: 'center' }} alignItems="center">
          <Grid item xs={12} sm={2} sx={{ textAlign: 'left' }}>
            <p style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <b>Sample Name</b>
                <Tooltip 
                    title="Filenames are used to identify uploaded samples - please ensure no PHI/PII is included in provided filenames."
                    arrow
                >
                    <InfoIcon 
                        sx={{ 
                            color: 'rgba(0, 0, 0, 0.4)',
                            fontSize: 20,
                            cursor: 'pointer'
                        }} 
                    />
                </Tooltip>
            </p>
          </Grid>
          <Grid item xs={1.5} sx={{ textAlign: 'center' }}>
            <p>Sample Type</p>
          </Grid>
          <Grid item xs={12} sm={2} sx={{ textAlign: 'center' }}>
            <p>{isIllumina ? 'R1 ' : ''}File Name</p>
          </Grid>
        {
            isIllumina ? 
            (
              <Grid item xs={12} sm={2} sx={{ textAlign: 'center' }}>
                <p>R2 File Name</p>
              </Grid>
            ) : null

        }
          <Grid item xs={12} sm={1.5} sx={{ textAlign: 'center' }}>
            <p>{isIllumina ? 'Total ' : ''}File Size</p>
          </Grid>
          {isIllumina ? null : (
          <Grid item xs={12} sm={2} sx={{ textAlign: 'center' }}>
            <p>File Last Modified</p>
          </Grid>
          )}
          <Grid item xs={12} sm={3} sx={{ textAlign: 'right', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1.5 }}>
            {!isIllumina && (
                <>
                    <FormControlLabel
                        control={<Checkbox size="small" checked={allowLowQuality} onChange={handleLowQualityToggle} />}
                        label="Allow Low Quality Data"
                        sx={{
                          m: 0,
                          mr: '12px',
                          '& .MuiFormControlLabel-label': { fontSize: '12px', lineHeight: 1.2 },
                          alignItems: 'center'
                        }}
                    />
                    <Box sx={{ borderLeft: '1px solid #d0d0d0', height: '18px', mr: '12px' }} />
                </>
            )}
            <Button
                size="small"
                sx={{ minWidth: 0, padding: '2px 8px', lineHeight: 1, fontSize: '12px' }}
                variant="contained"
                color="primary"
                aria-label="upload-all"
                startIcon={<UploadIcon sx={{ fontSize: 18 }} />}
                disabled={uploadAllDisabled}
                onClick={handleUploadAll}
            >
                Upload all
            </Button>
          </Grid>
        </Grid>
      </Box>
      <List sx={{
          width: '100%', bgcolor: 'background.paper', paddingTop: '0', paddingBottom: '0', maxHeight: "200px", overflowY: 'auto'
      }}
      >
        {Object.values(stagedFilesByDataset[activeRun?.id]).map(stagedFile => {
            return <AddSamplesFileListItem key={`${activeRun?.id}-${stagedFile.sampleName}`} activeRun={activeRun} isIllumina={isIllumina} stagedFile={stagedFile} removeStagedFile={removeStagedFile} setStagedFileAttribute={setStagedFileAttribute} />
        })}
      </List>
                </>
    ) : null}
        <Divider sx={{margin: '40px 40px 0 40px'}} />
            </DialogTitle>
            <DialogContent>
      <Grid container alignItems="center" sx={{ marginBottom: '20px' }}>
        <Grid item xs={12} sm={6}>
            <p style={{color: '#797979', marginBottom: 0}}>
                <b>SAMPLES IN DATASET</b>
            </p>
        </Grid>
        <Grid item xs={12} sm={6} sx={{ textAlign: 'right' }}>
            {!analysesLoading && hiddenSamples.length > 0 ? (
                <Typography variant="body2" sx={{ color: '#797979' }}>
                    {showHiddenSamples
                        ? 'Unclassified and low yield samples are visible.'
                        : `Unclassified and low yield samples hidden (${hiddenSamples.length}).`}
                    {' '}
                    <Link
                        component="button"
                        type="button"
                        onClick={() => setShowHiddenSamples(prev => !prev)}
                        sx={{ fontSize: 'inherit' }}
                    >
                        {showHiddenSamples ? 'Hide' : 'Show'}
                    </Link>
                </Typography>
            ) : null}
        </Grid>
      </Grid>
          <SamplesList
            samples={displayedSamples}
            activeProject={activeProject}
            selectedRun={activeRun}
            isIllumina={isIllumina}
          />
            </DialogContent>
            <DialogActions sx={{marginBottom: "15px"}}>
                {
                    backgroundUploadingFilesCt === 0 ?
                        null :
                        (
                            <p style={{color: "gray", marginRight: "20px", textAlign: "right"}}><small>Files may take a few minutes to upload<br />Feel free to close and upload{backgroundUploadingFilesCt === 1 ? '' : 's'} will continue in the background</small></p>
                        )
                }
                <Button variant="contained" aria-label="close" color="primary" sx={{marginRight: "20px"}} onClick={() => {
                    handleClose()
                }} >Close</Button>
            </DialogActions>
            <Dialog data-cy="unsaved-changes-dialog" open={confirmClose} onClose={() => setConfirmClose(false)}>
                <DialogTitle>Unsaved Changes</DialogTitle>
                <DialogContent>
                    You have {getStagedFilesNotUploadedCt()} file{getStagedFilesNotUploadedCt() === 1 ? '' : 's'} staged for upload that have not been uploaded. Are you sure you want to exit without uploading?
                </DialogContent>
                <DialogActions>
                    <Button aria-label="cancel-close" onClick={() => setConfirmClose(false)} variant="contained">Cancel</Button>
                    <Button aria-label="confirm-close" onClick={handleConfirmClose} >Exit Without Uploading</Button>
                </DialogActions>
            </Dialog>
            <Dialog open={showLowQualityDialog} onClose={() => { setShowLowQualityDialog(false); setAllowLowQuality(false); }}>
                <DialogTitle>Confirm Low Quality Data</DialogTitle>
                <DialogContent>
                    <p>
                        <b>Lower stringency filtering will be applied.</b> Results may be impacted. Proceed only if you intend to allow low-quality data.
                    </p>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => { setShowLowQualityDialog(false); setAllowLowQuality(false); }} variant="contained">Cancel</Button>
                    <Button onClick={() => { setShowLowQualityDialog(false); setAllowLowQuality(true); }} >OK</Button>
                </DialogActions>
            </Dialog>
        </Dialog>
        
    );
}

function AddSamplesDialogMenu(props) {
    const {
        activeRun,
        isIllumina,
        setArchiveDialogOpen,
    } = props;

    const [menuAnchorEl, setMenuAnchorEl] = useState(null);
    const [menuItemIsExporting, setMenuItemIsExporting] = useState(true);

    const { data: samples, isLoading: samplesLoading } = useSamples(activeRun?.id);
    const { data: activeAnalyses, isLoading: activeAnalysesLoading } = useActiveAnalysis(
        samples?.map(sample => sample.id)
    )

    useEffect(() => {
        setMenuItemIsExporting(true);
        if (samplesLoading || activeAnalysesLoading || !samples) {return;}

        const completeAnalyses = activeAnalyses.filter(analysis => analysis?.attributes.status === 'succeeded');
        if (completeAnalyses.length) setMenuItemIsExporting(false);
    }, [activeAnalysesLoading, samples, samplesLoading, activeAnalyses, setMenuItemIsExporting]);

    const handleMenuClick = (event) => {
        setMenuAnchorEl(event.currentTarget);
      };
      
    const handleMenuClose = (event) => {
        event?.preventDefault();
        event?.stopPropagation();
        setMenuAnchorEl(null);
    };
    
    const handleArchiveClick = () => {
        setArchiveDialogOpen(true);
        handleMenuClose();
    };


    return <>
    <IconButton
        size="small"
        onClick={handleMenuClick}
        sx={{ ml: 1 }}
        aria-label="sample-dialog-menu-button"
    >
    <MoreVertIcon />
    </IconButton>
    <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        onClick={handleMenuClick}
        anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
        }}
        transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
        }}
    >
    <ExportIdentifiedSpeciesMenuItem
        isIllumina={isIllumina}
        isDatasetLevel={true}
        run={activeRun}
        samples={samples}
        handleMenuClose={handleMenuClose}
        oneMenuItemClicked={menuItemIsExporting}
        setOneMenuItemClicked={setMenuItemIsExporting}
    />
    <ExportKgASTPredictionsMenuItem
        isIllumina={isIllumina}
        isDatasetLevel={true}
        run={activeRun}
        samples={samples}
        handleMenuClose={handleMenuClose}
        oneMenuItemClicked={menuItemIsExporting}
        setOneMenuItemClicked={setMenuItemIsExporting}
    />
    <ExportResistanceMarkersMenuItem
        isIllumina={isIllumina}
        isDatasetLevel={true}
        run={activeRun}
        samples={samples}
        handleMenuClose={handleMenuClose}
        oneMenuItemClicked={menuItemIsExporting}
        setOneMenuItemClicked={setMenuItemIsExporting}
    />
    <MenuItem aria-label="archive-dataset" onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        handleArchiveClick();
        handleMenuClose();
    }}>
        Archive Dataset
    </MenuItem>
    </Menu>
    </>
}

export default AddSamplesDialog;
